import {
    BrowserRouter,
    Routes,
    Route,
    useParams,
    useLocation
} from "react-router-dom";

import { lazy, Suspense, useEffect } from "react";

import { Provider, useDispatch, useSelector } from "react-redux";
import { store } from "./redux/store";

import Layout, { useRouteLangId } from "./components/Layout";
import BodyClass from "./components/BodyClass";
import ScrollToTop from "./pages/ScrollToTop";

import { HelmetProvider } from "react-helmet-async";

import { FETCH_LANGUAGES_REQUEST } from "./redux/type";
import { BASE_PATH } from "./components/config";
import NotFound from "./pages/NotFound";
import { ensureSagaGroups } from "./redux/routeSagas";

const lazyWithSagas = (pageImport, sagaGroups = []) => lazy(async () => {
    await ensureSagaGroups(sagaGroups);
    return pageImport();
});

const Home = lazy(() => import("./pages/Home"));
const ProductListing = lazyWithSagas(() => import("./pages/ProductListing"), ["listing"]);
const ShopDetail = lazyWithSagas(() => import("./pages/ShopDetail"), ["product"]);
const Cart = lazyWithSagas(() => import("./pages/Cart"), ["cart"]);
const Checkout = lazyWithSagas(() => import("./pages/Checkout"), ["checkout"]);
const ThankYou = lazyWithSagas(() => import("./pages/ThankYou"), ["thankYou"]);
const OrderCancelled = lazy(() => import("./pages/OrderCancelled"));
const Contact = lazyWithSagas(() => import("./pages/Contact"), ["contact"]);
const DataProtection = lazyWithSagas(() => import("./pages/DataProtection"), ["dataProtection"]);
const TermsConditions = lazyWithSagas(() => import("./pages/TermsConditions"), ["terms"]);
const GalleryCollection = lazyWithSagas(() => import("./pages/GalleryCollection"), ["gallery"]);
const OurLab = lazyWithSagas(() => import("./pages/OurLab"), ["lab"]);
const Frames = lazyWithSagas(() => import("./pages/Frames"), ["frames"]);
const Quality = lazyWithSagas(() => import("./pages/Quality"), ["quality"]);
const Signup = lazy(() => import("./pages/Signup"));
const Account = lazy(() => import("./pages/Account"));
const OrderListing = lazy(() => import("./pages/OrderListing"));
const OrderDetail = lazyWithSagas(() => import("./pages/OrderDetail"), ["orderDetail"]);
const Addresses = lazy(() => import("./pages/Addresses"));
const AddAddresses = lazy(() => import("./pages/AddAddresses"));
const VouchersListing = lazy(() => import("./pages/VouchersListing"));
const Sitemap = lazy(() => import("./pages/Sitemap"));
const WishlistPage = lazyWithSagas(() => import("./pages/WishlistPage"), ["wishlist"]);
const EditAddresses = lazy(() => import("./pages/EditAddress"));
const CreditSlipsPage = lazyWithSagas(() => import("./pages/CreditSlipsPage"), ["creditSlips"]);
const DefaultPage = lazy(() => import("./pages/DefaultPage"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const StoryPage = lazy(() => import("./pages/StoryPage"));
const StoriesIndex = lazy(() => import("./pages/StoriesIndex"));
// import ShopDetailNew from "./pages/ShopDetailNew";
const SUPPORTED_LANGUAGES = ["en", "nl", "de"];

// Stories and guides (module ps_stories): the segment is translated, /nl/verhalen, /en/stories, /de/geschichten.
const STORY_SEGMENTS = {
    story: ["stories", "verhalen", "geschichten"],
    guide: ["guides", "gidsen", "ratgeber"],
};

function LanguageLayout() {
    const { lang } = useParams();

    if (!SUPPORTED_LANGUAGES.includes(String(lang).toLowerCase())) {
        return  (
        <NotFound />
    );
    }

    return <Layout />;
}
function AppRoutes() {
    const dispatch = useDispatch();

    const { languages, error, loading } = useSelector(
        (s) => s.languages
    );

    const location = useLocation();
    const isHomeRoute = /^\/(?:en|nl|de)?\/?$/i.test(location.pathname);

    // lang_id of the language in the current URL (undefined until languages load)
    const routeLangId = useRouteLangId();
    const languagesFailed = !loading && !!error && !languages?.length;

    // Languages API
    useEffect(() => {
        dispatch({ type: FETCH_LANGUAGES_REQUEST });
    }, [dispatch]);

    // Common content: fetch once per language (not on every navigation).
    // Page data (home, static pages, product detail, listing) is fetched by
    // the page components themselves.
    useEffect(() => {
        if (routeLangId) {
            dispatch({ type: 'CcommonContent_sync', payload: { lang_id: routeLangId } });
        } else if (languagesFailed) {
            // Language list unavailable: fall back to the saga default
            dispatch({ type: 'CcommonContent_sync' });
        }
    }, [routeLangId, languagesFailed, dispatch]);

    return (
        <Suspense fallback={isHomeRoute ? (
          <section className="home-banner position-relative" aria-hidden="true">
            <div className="container">
              <div className="banner-slider home-banner-placeholder" />
            </div>
          </section>
        ) : null}>
        <Routes>

          {/* Default English */}
            <Route path="/" element={<Layout />}>

                <Route index element={<Home />} />

                <Route
                    path=":categorySlug?"
                    element={<ProductListing />}
                />
                <Route
                    path="shop/:categorySlug?"
                    element={<ProductListing />}
                />
                <Route path="cart" element={<Cart />} />
                <Route path="signup" element={<Signup />} />
                <Route path="account" element={<Account />} />

                <Route
                    path="order/:orderId"
                    element={<OrderDetail />}
                />

                <Route
                    path="wishlist"
                    element={<WishlistPage />}
                />

                <Route
                    path="my-orders"
                    element={<OrderListing />}
                />

                <Route
                    path="credit-slips"
                    element={<CreditSlipsPage />}
                />

                <Route path="sitemap" element={<Sitemap />} />

                <Route
                    path="my-vouchers"
                    element={<VouchersListing />}
                />

                <Route
                    path="my-addresses"
                    element={<Addresses />}
                />

                <Route
                    path="edit-address/:addressId"
                    element={<EditAddresses />}
                />

                <Route
                    path="add-address"
                    element={<AddAddresses />}
                />

                <Route path="checkout" element={<Checkout />} />

                <Route
                    path="contact-us"
                    element={<Contact />}
                />

                <Route
                    path="Data_Protection-Privacy_Policy"
                    element={<DataProtection />}
                />

                <Route
                    path="terms-conditions"
                    element={<TermsConditions />}
                />

                <Route
                    path="GettyImagesGallerycollection"
                    element={<GalleryCollection />}
                />

                <Route path="our-lab" element={<OurLab />} />
                <Route path="frames" element={<Frames />} />
                <Route path="quality-prints" element={<Quality />} />

                <Route
                    path="reset-password"
                    element={<ResetPassword />}
                />

                <Route
                    path=":categorySlug/:slug.html"
                    element={<ShopDetail />}
                />
                {/* <Route
                    path="new/:categorySlug/:slug.html"
                    element={<ShopDetailNew />}
                /> */}
                <Route
                    path="thank-you/:orderId"
                    element={<ThankYou />}
                />

                <Route
                    path="order-cancelled"
                    element={<OrderCancelled />}
                />

                <Route
                    path="page/:slug"
                    element={<DefaultPage />}
                />

                <Route path="*" element={<NotFound />} />

            </Route>
            {/* Language URLs */}
            <Route path="/:lang" element={<LanguageLayout />}>

                <Route index element={<Home />} />

                {Object.entries(STORY_SEGMENTS).flatMap(([type, segments]) =>
                    segments.flatMap((segment) => [
                        <Route key={segment} path={segment} element={<StoriesIndex key={segment} type={type} />} />,
                        <Route key={`${segment}-page`} path={`${segment}/:storySlug`} element={<StoryPage type={type} />} />,
                    ])
                )}

                <Route
                    path=":categorySlug?"
                    element={<ProductListing />}
                />
                <Route
                    path="shop/:categorySlug?"
                    element={<ProductListing />}
                />
                <Route
                    path="shop"
                    element={<ProductListing />}
                />
                <Route path="cart" element={<Cart />} />
                <Route path="signup" element={<Signup />} />
                <Route path="account" element={<Account />} />

                <Route
                    path="order/:orderId"
                    element={<OrderDetail />}
                />

                <Route
                    path="wishlist"
                    element={<WishlistPage />}
                />

                <Route
                    path="my-orders"
                    element={<OrderListing />}
                />

                <Route
                    path="credit-slips"
                    element={<CreditSlipsPage />}
                />

                <Route path="sitemap" element={<Sitemap />} />

                <Route
                    path="my-vouchers"
                    element={<VouchersListing />}
                />

                <Route
                    path="my-addresses"
                    element={<Addresses />}
                />

                <Route
                    path="edit-address/:addressId"
                    element={<EditAddresses />}
                />

                <Route
                    path="add-address"
                    element={<AddAddresses />}
                />

                <Route path="checkout" element={<Checkout />} />

                <Route
                    path="contact-us"
                    element={<Contact />}
                />

                <Route
                    path="Data_Protection-Privacy_Policy"
                    element={<DataProtection />}
                />

                <Route
                    path="terms-conditions"
                    element={<TermsConditions />}
                />

                <Route
                    path="GettyImagesGallerycollection"
                    element={<GalleryCollection />}
                />

                <Route path="our-lab" element={<OurLab />} />
                <Route path="frames" element={<Frames />} />
                <Route path="quality-prints" element={<Quality />} />

                <Route
                    path="reset-password"
                    element={<ResetPassword />}
                />

                <Route
                    path=":categorySlug/:slug.html"
                    element={<ShopDetail />}
                />

                {/* <Route
                    path="new/:categorySlug/:slug.html"
                    element={<ShopDetailNew />}
                /> */}

                <Route
                    path="thank-you/:orderId"
                    element={<ThankYou />}
                />

                <Route
                    path="order-cancelled"
                    element={<OrderCancelled />}
                />

                <Route
                    path="page/:slug"
                    element={<DefaultPage />}
                />

                <Route path="*" element={<NotFound />} />

            </Route>
            

            

        </Routes>
        </Suspense>
  );
}

function App() {
  return (
    <Provider store={store}>
      <HelmetProvider>
        <BrowserRouter basename={`${BASE_PATH}/`}>
          <BodyClass />
          <ScrollToTop />

          <AppRoutes />

        </BrowserRouter>
      </HelmetProvider>
    </Provider>
  );
}

export default App;
